
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { useTheme } from 'next-themes';

interface SystemSettingsProps {
  onSave: () => void;
}

export function SystemSettings({ onSave }: SystemSettingsProps) {
  const { theme, setTheme } = useTheme();
  
  const [dateFormat, setDateFormat] = useState('MM/dd/yyyy');
  const [defaultCertRetention, setDefaultCertRetention] = useState('5');
  const [defaultReportRetention, setDefaultReportRetention] = useState('3');
  const [autoRunRetention, setAutoRunRetention] = useState(true);
  const [defaultDashboardView, setDefaultDashboardView] = useState('overview');
  
  const handleSaveSettings = () => {
    // In a production app, this would update settings in the database
    console.log('Saving system settings:', {
      theme,
      dateFormat,
      defaultCertRetention,
      defaultReportRetention,
      autoRunRetention,
      defaultDashboardView
    });
    
    // Call the onSave callback to trigger toast notification
    onSave();
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Appearance</CardTitle>
          <CardDescription>
            Customize the appearance of the admin dashboard
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="theme">Theme</Label>
              <Select 
                value={theme} 
                onValueChange={(value) => setTheme(value)}
              >
                <SelectTrigger id="theme">
                  <SelectValue placeholder="Select theme" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="light">Light</SelectItem>
                  <SelectItem value="dark">Dark</SelectItem>
                  <SelectItem value="system">System</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Choose between light, dark, or system default theme
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="date-format">Date Format</Label>
              <Select 
                value={dateFormat} 
                onValueChange={setDateFormat}
              >
                <SelectTrigger id="date-format">
                  <SelectValue placeholder="Select date format" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="MM/dd/yyyy">MM/DD/YYYY</SelectItem>
                  <SelectItem value="dd/MM/yyyy">DD/MM/YYYY</SelectItem>
                  <SelectItem value="yyyy-MM-dd">YYYY-MM-DD</SelectItem>
                  <SelectItem value="MMMM d, yyyy">Month D, YYYY</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Set the default date format for the application
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Default Views</CardTitle>
          <CardDescription>
            Configure default views and display preferences
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="default-dashboard">Default Dashboard View</Label>
              <Select 
                value={defaultDashboardView} 
                onValueChange={setDefaultDashboardView}
              >
                <SelectTrigger id="default-dashboard">
                  <SelectValue placeholder="Select default dashboard view" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="overview">Overview</SelectItem>
                  <SelectItem value="certificates">Certificates</SelectItem>
                  <SelectItem value="compliance">Compliance</SelectItem>
                  <SelectItem value="retention">Retention</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Select which view to show by default when opening the dashboard
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Retention Policy Defaults</CardTitle>
          <CardDescription>
            Configure default retention periods and settings
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="cert-retention">Default Certificate Retention (years)</Label>
              <Input 
                id="cert-retention" 
                type="number"
                min="1"
                max="10"
                value={defaultCertRetention}
                onChange={(e) => setDefaultCertRetention(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                How long certificates should be kept before archiving
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="report-retention">Default Report Retention (years)</Label>
              <Input 
                id="report-retention" 
                type="number"
                min="1"
                max="10"
                value={defaultReportRetention}
                onChange={(e) => setDefaultReportRetention(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                How long reports should be kept before archiving
              </p>
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="auto-retention" className="text-base">Automatic Retention Processing</Label>
              <p className="text-sm text-muted-foreground">
                Automatically process retention policies on schedule
              </p>
            </div>
            <Switch
              id="auto-retention"
              checked={autoRunRetention}
              onCheckedChange={setAutoRunRetention}
            />
          </div>
        </CardContent>
      </Card>
      
      <div className="flex justify-end">
        <Button onClick={handleSaveSettings}>
          Save System Settings
        </Button>
      </div>
    </div>
  );
}
